import { z } from 'zod';
import { resourceKeySchema, timestampSchema } from './common';
import { linkEntityKindSchema } from './links';

/**
 * Where something is along a line.
 *
 * Most of what an infrastructure project tracks is positioned along a route
 * rather than at a point: a track renewal runs between two chainages, a
 * resurfacing covers a stretch of carriageway, a pipeline weld sits at a
 * distance from the origin. The map already answers "where" in latitude and
 * longitude, which is correct and is not the form anybody working on the ground
 * uses. They say `245+300`.
 *
 * ## Why this is not a railway feature
 *
 * It is opt-in *by data*, not by configuration. A project with no reference
 * lines never sees a chainage control anywhere in the product -- no setting to
 * find, no module to switch, nothing to explain to a client who does not have a
 * linear network. A project that defines one gets the fields, the column and
 * the range filter, and every one of them disappears again if the line is
 * removed. That is the same rule `ProjectDivision` follows: the concept appears
 * when somebody creates one.
 *
 * Nothing here names an industry. A `ReferenceLine` is a named route with a
 * measure along it; whether that is a railway line, a road, a tunnel or a
 * conveyor is a matter of what somebody called it.
 *
 * ## Measures are integer metres
 *
 * Not floats. Chainage is quoted to the metre, and floating point turns
 * equality and ordering on survey values into a source of bugs that are
 * invisible until two people compare printouts. The display format is a
 * property of the *line*, so one project reads `245+300` and another reads
 * `245300 m` without either storing a different number.
 */

/* -------------------------------------------------------------- the line */

/**
 * How a measure along this line is written down.
 *
 * Two, and staying at two is deliberate. Every additional format is a parser, a
 * formatter, three translations and a way for two people to type the same
 * position differently.
 */
export const MEASURE_FORMATS = ['chainage', 'metre'] as const;
export const measureFormatSchema = z.enum(MEASURE_FORMATS);
export type MeasureFormat = (typeof MEASURE_FORMATS)[number];

/** A measure in metres from the line's origin. Bounded at 10 000 km, which is
 *  longer than any single route and short enough to catch a pasted digit. */
export const measureSchema = z.number().int().min(0).max(10_000_000);

export const referenceLineSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  key: resourceKeySchema,
  name: z.string(),
  description: z.string().nullable(),
  measureFormat: measureFormatSchema,
  /**
   * The extent of the line itself. A reference outside it is refused, which is
   * the cheapest way to catch the single most common data-entry mistake here:
   * a chainage from a different line entirely.
   */
  startMeasure: measureSchema,
  endMeasure: measureSchema,
  orderIndex: z.number().int(),
  /** Positions recorded on the line. One with any cannot be deleted. */
  positionCount: z.number().int().nonnegative(),
  version: z.number().int().nonnegative(),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});
export type ReferenceLine = z.infer<typeof referenceLineSchema>;

const lineWritableFields = z.object({
  name: z.string().min(1).max(200),
  description: z.string().max(2000).nullish(),
  measureFormat: measureFormatSchema.default('chainage'),
  startMeasure: measureSchema.default(0),
  endMeasure: measureSchema,
  orderIndex: z.number().int().min(0).max(10_000).default(0),
});

/** A line runs somewhere: an extent that ends where it starts is not a line. */
function refineExtent<T extends z.ZodTypeAny>(schema: T) {
  return schema.refine(
    (value) => {
      const record = value as { startMeasure?: number; endMeasure?: number };
      if (record.startMeasure === undefined || record.endMeasure === undefined) return true;
      return record.startMeasure < record.endMeasure;
    },
    { message: 'endMeasure must be greater than startMeasure', path: ['endMeasure'] },
  );
}

export const createReferenceLineSchema = refineExtent(
  lineWritableFields.extend({ key: resourceKeySchema }).strict(),
);
export type CreateReferenceLine = z.infer<typeof createReferenceLineSchema>;

/** `key` is absent for the reason it is absent from every other update schema
 *  in this product: a renameable key is not a key. */
export const updateReferenceLineSchema = refineExtent(
  lineWritableFields.partial().extend({ version: z.number().int().nonnegative() }).strict(),
);
export type UpdateReferenceLine = z.infer<typeof updateReferenceLineSchema>;

/* --------------------------------------------------------- the reference */

/**
 * One thing's position on one line.
 *
 * Addressed polymorphically, by the same `LinkEntityKind` vocabulary
 * `entity_link` and `meeting_agenda_reference` use. Its own table rather than
 * three columns on each of the kinds that can carry one, and the reason is the
 * query this whole feature exists to serve: *what is happening between km
 * 245+000 and km 250+000* is a question across kinds, and the answer has to be
 * one indexed range scan rather than a union of however many tables have
 * grown the columns so far.
 *
 * A point is an extent whose ends are equal, not a second shape. A signal at
 * `245+300` and a renewal from `245+300` to `247+150` are the same kind of
 * fact at different lengths, and modelling them separately would double every
 * filter and every formatter for no gain.
 */
export const linearReferenceSchema = z.object({
  id: z.string().uuid(),
  entityKind: linkEntityKindSchema,
  entityId: z.string().uuid(),
  lineId: z.string().uuid(),
  /** Denormalized for display, so a list does not join to render a label. */
  lineName: z.string(),
  lineMeasureFormat: measureFormatSchema,
  fromMeasure: measureSchema,
  toMeasure: measureSchema,
  /** Rendered by the API in the line's own format, so no client repeats it. */
  label: z.string(),
});
export type LinearReference = z.infer<typeof linearReferenceSchema>;

export const setLinearReferenceSchema = z
  .object({
    lineId: z.string().uuid(),
    fromMeasure: measureSchema,
    toMeasure: measureSchema,
  })
  .strict()
  .refine((value) => value.fromMeasure <= value.toMeasure, {
    message: 'toMeasure must not precede fromMeasure',
    path: ['toMeasure'],
  });
export type SetLinearReference = z.infer<typeof setLinearReferenceSchema>;

/**
 * Everything on a line between two measures, whatever kind it is.
 *
 * The query a section engineer runs. `lineId` is required: a range with no line
 * is a number with no meaning, and defaulting to "all lines" would silently
 * return work from a different route at the same chainage -- which is the one
 * wrong answer that looks exactly like a right one.
 */
export const linearRangeQuerySchema = z.object({
  lineId: z.string().uuid(),
  fromMeasure: z.coerce.number().int().min(0).max(10_000_000),
  toMeasure: z.coerce.number().int().min(0).max(10_000_000),
  entityKind: linkEntityKindSchema.optional(),
});
export type LinearRangeQuery = z.infer<typeof linearRangeQuerySchema>;

export const linearRangeHitSchema = z.object({
  entityKind: linkEntityKindSchema,
  entityId: z.string().uuid(),
  /** The thing's own name, resolved so the caller renders a list, not ids. */
  name: z.string(),
  fromMeasure: measureSchema,
  toMeasure: measureSchema,
  label: z.string(),
});
export type LinearRangeHit = z.infer<typeof linearRangeHitSchema>;

/**
 * Every position held by things of one kind, for a list to render a column
 * without a request per row.
 *
 * Deliberately not folded into `scheduleTaskSchema` alongside `stageId` and
 * `sectionId`, even though it reads like the same sort of property. Those are
 * columns on the task; a position is a row in another table that most projects
 * do not have at all. Embedding it would make the scheduling contract depend on
 * an optional feature, and every consumer of a task would carry a field that is
 * null for everyone without a linear network.
 */
export const linearReferenceListQuerySchema = z.object({
  entityKind: linkEntityKindSchema,
});
export type LinearReferenceListQuery = z.infer<typeof linearReferenceListQuerySchema>;

/* ------------------------------------------------------ reading and writing */

/**
 * `245300` -> `245+300`.
 *
 * The `+` notation is how a distance along a route is written wherever one is
 * maintained: the part before it counts whole kilometres and the part after it
 * the metres within, padded to three digits so `245+030` cannot be misread as
 * `245+300`. That padding is the entire reason this is a function rather than a
 * template string at each call site.
 */
export function formatMeasure(metres: number, format: MeasureFormat): string {
  if (format === 'metre') return `${metres} m`;
  const kilometres = Math.floor(metres / 1000);
  const remainder = metres % 1000;
  return `${kilometres}+${String(remainder).padStart(3, '0')}`;
}

/** An extent, or a single position when the ends agree. */
export function formatExtent(from: number, to: number, format: MeasureFormat): string {
  const start = formatMeasure(from, format);
  if (from === to) return start;
  /* An en dash, and no spaces around it: `245+300–247+150` reads as one range,
   * where the spaced form reads as two values that happen to be adjacent. */
  return `${start}–${formatMeasure(to, format)}`;
}

/**
 * `245+300`, `245 + 300`, `245300`, `245.3 km` -> `245300`.
 *
 * Deliberately forgiving about how somebody types a position and strict about
 * what it means. People transcribe chainage from drawings, emails and site
 * notes, and refusing a space around the `+` teaches them to distrust the field
 * rather than to type it differently.
 *
 * Returns null rather than throwing: this parses input somebody is still
 * typing, and the caller renders a hint rather than an error.
 */
export function parseMeasure(input: string, format: MeasureFormat): number | null {
  const trimmed = input.trim().replace(/\s+/g, '');
  if (trimmed === '') return null;

  /* `245+300`, in either format -- somebody reading a drawing types what the
   * drawing says, whatever the line was configured to display. */
  const plus = /^(\d{1,5})\+(\d{1,3})$/.exec(trimmed);
  if (plus) {
    const metres = Number(plus[2]);
    /* `245+1500` is not 245 km and 1500 m; it is a typo, and silently carrying
     * it into the next kilometre would put the work somewhere nobody meant. */
    if (metres > 999) return null;
    return Number(plus[1]) * 1000 + metres;
  }

  /* `245.3 km` and `245,3 km`, which is how a decimal keyboard produces it. */
  const km = /^(\d{1,5})[.,](\d{1,3})km?$/i.exec(trimmed);
  if (km) {
    const fraction = km[2]!.padEnd(3, '0');
    return Number(km[1]) * 1000 + Number(fraction);
  }

  const plain = /^(\d{1,8})m?$/i.exec(trimmed);
  if (plain) {
    const value = Number(plain[1]);
    /* In chainage format a bare number is metres, not kilometres: `245300` is
     * what the stored value looks like, and a reader pasting one back must get
     * the position they copied. */
    return format === 'metre' || value > 9999 ? value : value * 1000;
  }

  return null;
}
